// communityManage/styled.js
import { styled } from '@mui/material/styles';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  TextField, 
  FormControl,
  Select,
  Switch,
  Chip,
  Card,
  CardContent
} from '@mui/material';

// Import honey colors from your existing styled components
export const honeyColors = {
  primary: '#F1C40F',
  primaryLight: '#F4D03F',
  primaryDark: '#D4AC0D',
  amber: '#FFB74D',
  cream: '#FFF8E1',
  honeycomb: '#D4A574',
  caramel: '#8D6E63',
  success: '#66BB6A',
  warning: '#FF9800',
  info: '#29B6F6',
  error: '#EF5350',
  background: '#FFFEF7',
  surface: '#FFFFFF',
  surfaceHoney: '#FFF9E6',
  border: '#F5E6B3',
  textPrimary: '#3E2723',
  textSecondary: '#5D4037',
  textMuted: '#8D6E63',
  gradientPrimary: 'linear-gradient(135deg, #F1C40F 0%, #FFB74D 100%)',
  gradientHoneycomb: 'linear-gradient(135deg, #D4A574 0%, #F1C40F 50%, #FFB74D 100%)',
  gradientGlass: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
  shadowLight: '0 2px 8px rgba(212, 165, 116, 0.15)',
  shadowMedium: '0 4px 16px rgba(212, 165, 116, 0.25)',
  shadowHeavy: '0 8px 32px rgba(212, 165, 116, 0.35)',
  shadowGlow: '0 0 20px rgba(241, 196, 15, 0.3)',
};

export const ManageContainer = styled(Box)({
  padding: '32px',
  backgroundColor: honeyColors.background,
  minHeight: '100vh',
});

export const ManageHeader = styled(Box)({
  marginBottom: '32px',
  padding: '24px',
  backgroundColor: honeyColors.surface,
  borderRadius: '20px',
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowLight,
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: honeyColors.gradientPrimary,
  },
});

export const ManageTitle = styled(Typography)({
  fontSize: '2rem !important',
  fontWeight: '800 !important',
  color: honeyColors.textPrimary,
  marginBottom: '8px !important',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  
  '& .MuiSvgIcon-root': {
    color: honeyColors.primary,
    fontSize: '2rem',
  },
});

export const ManageSubtitle = styled(Typography)({
  color: honeyColors.textSecondary,
  fontSize: '1.1rem !important',
  fontWeight: '500 !important',
});

export const SectionCard = styled(Card)({
  marginBottom: '24px',
  backgroundColor: honeyColors.surface,
  borderRadius: '20px',
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowLight,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: honeyColors.gradientPrimary,
  },
  
  '&:hover': {
    borderColor: honeyColors.primary,
    boxShadow: honeyColors.shadowMedium,
    transform: 'translateY(-2px)',
  },
});

export const SectionCardContent = styled(CardContent)({
  padding: '32px !important',
});

export const SectionTitle = styled(Typography)({
  fontSize: '1.5rem !important',
  fontWeight: '700 !important',
  color: honeyColors.textPrimary,
  marginBottom: '20px !important',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  
  '& .MuiSvgIcon-root': {
    color: honeyColors.primary,
    fontSize: '1.5rem',
  },
});

export const FormGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '24px',
  marginBottom: '24px',
});

export const FormRow = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginBottom: '24px',
});

export const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: honeyColors.surfaceHoney,
    border: `2px solid ${honeyColors.border}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    
    '&:hover': {
      borderColor: honeyColors.primary,
      backgroundColor: honeyColors.surface,
    },
    
    '&.Mui-focused': {
      borderColor: honeyColors.primary,
      backgroundColor: honeyColors.surface,
      boxShadow: honeyColors.shadowGlow,
    },
    
    '& fieldset': {
      border: 'none',
    },
  },
  
  '& .MuiInputLabel-root': {
    color: honeyColors.textSecondary,
    fontWeight: 600,
    
    '&.Mui-focused': {
      color: honeyColors.primary,
    },
  },
});

export const StyledFormControl = styled(FormControl)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: honeyColors.surfaceHoney,
    border: `2px solid ${honeyColors.border}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    
    '&:hover': {
      borderColor: honeyColors.primary,
      backgroundColor: honeyColors.surface,
    },
    
    '&.Mui-focused': {
      borderColor: honeyColors.primary,
      backgroundColor: honeyColors.surface,
      boxShadow: honeyColors.shadowGlow,
    },
    
    '& fieldset': {
      border: 'none',
    },
  },
  
  '& .MuiInputLabel-root': {
    color: honeyColors.textSecondary,
    fontWeight: 600,
    
    '&.Mui-focused': {
      color: honeyColors.primary,
    },
  },
});

export const StyledSelect = styled(Select)({
  borderRadius: '12px !important',
});

export const SwitchContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  backgroundColor: honeyColors.surfaceHoney,
  borderRadius: '12px',
  border: `2px solid ${honeyColors.border}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    borderColor: honeyColors.primary,
    backgroundColor: honeyColors.surface,
  },
});

export const SwitchLabel = styled(Typography)({
  fontWeight: '600 !important',
  color: honeyColors.textPrimary,
  fontSize: '1rem !important',
});

export const SwitchDescription = styled(Typography)({
  color: honeyColors.textSecondary,
  fontSize: '0.9rem !important',
  marginTop: '4px !important',
});

export const StyledSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      color: honeyColors.primary,
      
      '& + .MuiSwitch-track': {
        backgroundColor: honeyColors.primaryLight,
        opacity: 1,
      },
    },
  },
  
  '& .MuiSwitch-track': {
    backgroundColor: honeyColors.border,
    borderRadius: '12px',
  },
});

export const TagsContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '12px',
});

export const StyledChip = styled(Chip)({
  backgroundColor: honeyColors.surfaceHoney,
  color: honeyColors.textPrimary,
  fontWeight: 600,
  border: `2px solid ${honeyColors.border}`,
  borderRadius: '12px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    backgroundColor: honeyColors.primary,
    borderColor: honeyColors.primaryDark,
    color: honeyColors.textPrimary,
    transform: 'translateY(-1px)',
    boxShadow: honeyColors.shadowLight,
  },
  
  '& .MuiChip-deleteIcon': {
    color: honeyColors.textSecondary,
    
    '&:hover': {
      color: honeyColors.error,
    },
  },
});

export const ActionButtonsContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
  justifyContent: 'flex-end',
  marginTop: '32px',
  paddingTop: '24px',
  borderTop: `2px solid ${honeyColors.border}`,
});

export const SaveButton = styled(Button)({
  borderRadius: '16px',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '1rem',
  padding: '14px 32px',
  background: honeyColors.gradientPrimary,
  color: honeyColors.textPrimary,
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowMedium,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    background: honeyColors.gradientHoneycomb,
    transform: 'translateY(-2px)',
    boxShadow: honeyColors.shadowHeavy,
    borderColor: honeyColors.primary,
  },
  
  '&:disabled': {
    background: honeyColors.border,
    color: honeyColors.textMuted,
    boxShadow: 'none',
    transform: 'none',
  },
});

export const CancelButton = styled(Button)({
  borderRadius: '16px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  padding: '14px 32px',
  backgroundColor: 'transparent',
  color: honeyColors.textSecondary,
  border: `2px solid ${honeyColors.border}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    backgroundColor: honeyColors.surfaceHoney,
    borderColor: honeyColors.primary,
    color: honeyColors.textPrimary,
  },
});

export const UploadContainer = styled(Box)({
  border: `2px dashed ${honeyColors.border}`,
  borderRadius: '16px',
  padding: '32px',
  textAlign: 'center',
  backgroundColor: honeyColors.surfaceHoney,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  
  '&:hover': {
    borderColor: honeyColors.primary,
    backgroundColor: honeyColors.surface,
    borderStyle: 'solid',
  },
  
  '& .MuiSvgIcon-root': {
    fontSize: '3rem',
    color: honeyColors.primary,
    marginBottom: '16px',
  },
});

export const UploadText = styled(Typography)({
  color: honeyColors.textSecondary,
  fontWeight: '600 !important',
  marginBottom: '8px !important',
});

export const UploadSubtext = styled(Typography)({
  color: honeyColors.textMuted,
  fontSize: '0.9rem !important',
});

export const ImagePreview = styled(Box)({
  width: '120px',
  height: '120px',
  borderRadius: '16px',
  overflow: 'hidden',
  border: `2px solid ${honeyColors.border}`,
  marginTop: '16px',
  position: 'relative',
  
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

export const DeleteImageButton = styled(Button)({
  position: 'absolute',
  top: '8px',
  right: '8px',
  minWidth: '32px',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: honeyColors.error,
  color: 'white',
  boxShadow: honeyColors.shadowMedium,
  
  '&:hover': {
    backgroundColor: honeyColors.error,
    transform: 'scale(1.1)',
  },
  
  '& .MuiSvgIcon-root': {
    fontSize: '1.2rem',
  },
});

export const StatusIndicator = styled(Box)(({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'AC': return honeyColors.success;
      case 'PP': return honeyColors.warning;
      case 'IN': return honeyColors.textMuted;
      case 'RE': return honeyColors.error;
      case 'BA': return honeyColors.error;
      default: return honeyColors.textMuted;
    }
  };
  
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '12px',
    backgroundColor: honeyColors.surfaceHoney,
    border: `2px solid ${getStatusColor()}`,
    color: getStatusColor(),
    fontWeight: 600,
    
    '&::before': {
      content: '""',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: getStatusColor(),
    },
  };
});

export const StatsContainer = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
  marginBottom: '24px',
});

export const StatItem = styled(Box)({
  padding: '20px',
  backgroundColor: honeyColors.surfaceHoney,
  borderRadius: '16px',
  border: `2px solid ${honeyColors.border}`,
  textAlign: 'center',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    borderColor: honeyColors.primary,
    transform: 'translateY(-2px)',
    boxShadow: honeyColors.shadowLight,
  },
});

export const StatValue = styled(Typography)({
  fontSize: '2rem !important',
  fontWeight: '800 !important',
  color: honeyColors.primary,
  lineHeight: 1,
});

export const StatLabel = styled(Typography)({
  color: honeyColors.textSecondary,
  fontWeight: '600 !important',
  marginTop: '8px !important',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontSize: '0.9rem !important',
});

// NEW: Rich Text Editor Components
export const RichTextEditorContainer = styled(Box)({
  marginBottom: '24px',
  
  '& .ql-toolbar.ql-snow': {
    border: `2px solid ${honeyColors.border} !important`,
    borderBottom: 'none !important',
    borderRadius: '12px 12px 0 0 !important',
    backgroundColor: `${honeyColors.surfaceHoney} !important`,
    padding: '12px 16px !important',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
  },
  
  '& .ql-container.ql-snow': {
    border: `2px solid ${honeyColors.border} !important`,
    borderTop: 'none !important',
    borderRadius: '0 0 12px 12px !important',
    backgroundColor: `${honeyColors.surface} !important`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
  },
  
  '& .ql-editor': {
    fontSize: '14px !important',
    lineHeight: '1.5 !important',
    color: `${honeyColors.textPrimary} !important`,
    padding: '16px 20px !important',
    minHeight: '120px !important',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
  },
  
  '& .ql-editor.ql-blank::before': {
    color: `${honeyColors.textMuted} !important`,
    fontSize: '14px !important',
    fontStyle: 'italic !important',
    left: '20px !important',
    fontWeight: '500 !important',
  },
  
  '&:hover .ql-toolbar.ql-snow': {
    borderColor: `${honeyColors.primary} !important`,
    backgroundColor: `${honeyColors.surface} !important`,
  },
  
  '&:hover .ql-container.ql-snow': {
    borderColor: `${honeyColors.primary} !important`,
  },
  
  '&:focus-within .ql-toolbar.ql-snow': {
    borderColor: `${honeyColors.primary} !important`,
  },
  
  '&:focus-within .ql-container.ql-snow': {
    borderColor: `${honeyColors.primary} !important`,
    boxShadow: `${honeyColors.shadowGlow} !important`,
  },
});

export const RichTextEditorLabel = styled(Typography)({
  color: honeyColors.textSecondary,
  fontWeight: '600 !important',
  marginBottom: '8px !important',
  fontSize: '0.875rem !important',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
    color: honeyColors.textMuted,
  },
});

export const RichTextEditorError = styled(Typography)({
  color: `${honeyColors.error} !important`,
  fontSize: '0.75rem !important',
  marginTop: '8px !important',
  marginLeft: '14px !important',
  fontWeight: '500 !important',
});

// Enhanced Rich Text Editor with error state
export const RichTextEditorWithError = styled(Box)(({ hasError }) => ({
  marginBottom: '24px',
  
  '& .ql-toolbar.ql-snow': {
    border: `2px solid ${hasError ? honeyColors.error : honeyColors.border} !important`,
    borderBottom: 'none !important',
    borderRadius: '12px 12px 0 0 !important',
    backgroundColor: `${honeyColors.surfaceHoney} !important`,
    padding: '12px 16px !important',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
  },
  
  '& .ql-container.ql-snow': {
    border: `2px solid ${hasError ? honeyColors.error : honeyColors.border} !important`,
    borderTop: 'none !important',
    borderRadius: '0 0 12px 12px !important',
    backgroundColor: `${honeyColors.surface} !important`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
  },
  
  '& .ql-editor': {
    fontSize: '14px !important',
    lineHeight: '1.5 !important',
    color: `${honeyColors.textPrimary} !important`,
    padding: '16px 20px !important',
    minHeight: '120px !important',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
  },
  
  '& .ql-editor.ql-blank::before': {
    color: `${honeyColors.textMuted} !important`,
    fontSize: '14px !important',
    fontStyle: 'italic !important',
    left: '20px !important',
    fontWeight: '500 !important',
  },
  
  '&:hover .ql-toolbar.ql-snow': {
    borderColor: `${hasError ? honeyColors.error : honeyColors.primary} !important`,
    backgroundColor: `${honeyColors.surface} !important`,
  },
  
  '&:hover .ql-container.ql-snow': {
    borderColor: `${hasError ? honeyColors.error : honeyColors.primary} !important`,
  },
  
  '&:focus-within .ql-toolbar.ql-snow': {
    borderColor: `${hasError ? honeyColors.error : honeyColors.primary} !important`,
  },
  
  '&:focus-within .ql-container.ql-snow': {
    borderColor: `${hasError ? honeyColors.error : honeyColors.primary} !important`,
    boxShadow: hasError 
      ? `0 0 20px rgba(239, 83, 80, 0.3) !important`
      : `${honeyColors.shadowGlow} !important`,
  },
}));